import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, updateLastActivity, checkUserAuthorization } from '../../store/authSlice';

const INACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const PrivateRoute = ({ children }) => {
  const dispatch = useDispatch();
  const { 
    isAuthenticated, 
    lastActivityTimestamp, 
    isAuthorized 
  } = useSelector((state) => state.auth);
  const token = localStorage.getItem('token');

  // Throttle activity tracking
  const activityTimeout = 10000; // 10 seconds
  const lastActivityRef = useRef(Date.now());

  // Check authorization periodically
  useEffect(() => {
    if (isAuthenticated && token) {
      const checkAuth = async () => {
        try {
          await dispatch(checkUserAuthorization()).unwrap();
        } catch (error) {
          console.error('Authorization check failed:', error);
          if (error.message === 'Token expired' || error.message === 'Invalid token') {
            dispatch(logout());
            localStorage.removeItem('token');
          }
        }
      };
      checkAuth();
    }
  }, [isAuthenticated, token, dispatch]);

  // Inactivity timeout logic
  useEffect(() => {
    if (isAuthenticated && lastActivityTimestamp) {
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - lastActivityTimestamp;

      if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
        dispatch(logout());
        localStorage.removeItem('token');
      }
    }
  }, [isAuthenticated, lastActivityTimestamp, dispatch]);

  // Throttled activity tracking
  useEffect(() => {
    const trackActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > activityTimeout) {
        dispatch(updateLastActivity());
        lastActivityRef.current = now;
      }
    };

    window.addEventListener('mousemove', trackActivity);
    window.addEventListener('keydown', trackActivity);
    window.addEventListener('click', trackActivity);

    return () => {
      window.removeEventListener('mousemove', trackActivity);
      window.removeEventListener('keydown', trackActivity);
      window.removeEventListener('click', trackActivity);
    };
  }, [dispatch]);

  // Check for authentication and authorization
  if (!token || !isAuthenticated || !isAuthorized) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default PrivateRoute;